import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { getApiErrorMessage } from '../../lib/apiError';
import AdminUserDetailModal from '../../components/admin/AdminUserDetailModal';
import { toast } from 'sonner';
import {
  Users,
  Star,
  MapPin,
  TrendingUp,
  CheckCircle2,
  Clock,
  Search,
  UserPlus,
  X,
  CalendarDays,
  ShieldOff,
  RotateCcw
} from 'lucide-react';

const StatCard = ({ label, value, color = 'burgundy', trend, icon: Icon }) => {
  const colorStyles = {
    burgundy: 'border-burgundy-100',
    green: 'border-green-100',
    blue: 'border-blue-100',
    yellow: 'border-yellow-100',
    red: 'border-red-100',
  };

  const iconColors = {
    burgundy: 'text-burgundy-600',
    green: 'text-green-600',
    blue: 'text-blue-600',
    yellow: 'text-yellow-600',
    red: 'text-red-600',
  };

  return (
    <div className={`bg-white rounded-2xl border ${colorStyles[color]} p-5`}>
      <div className="flex items-start justify-between mb-2">
        {Icon && <Icon className={`w-5 h-5 ${iconColors[color]}`} />}
        {trend && (
          <span className="text-xs text-green-600 flex items-center font-medium">
            <TrendingUp className="w-3 h-3 mr-0.5" />
            {trend}
          </span>
        )}
      </div>
      <p className="text-3xl font-serif font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500 mt-1 uppercase tracking-wide">{label}</p>
    </div>
  );
};

const officerName = (officer) => (
  [officer.firstName || officer.first_name, officer.lastName || officer.last_name].filter(Boolean).join(' ') || officer.email || 'Care Officer'
);

const normalizeStatus = (status) => String(status || 'active').trim();

const AdminOfficersPage = () => {
  const navigate = useNavigate();
  const { authApi } = useAuth();
  const { careOfficers, visits, dependants, ratings, fetchData } = useApp();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOfficer, setSelectedOfficer] = useState(null);
  const [detailUserId, setDetailUserId] = useState('');
  const [actionLoading, setActionLoading] = useState('');

  const officers = useMemo(() => careOfficers.map(officer => {
    const coverage = officer.coverageZone || officer.coverage_zone || {};
    return {
      ...officer,
      name: officerName(officer),
      statusLabel: normalizeStatus(officer.status),
      coverageState: coverage.state || officer.state || officer.assignedRegion || officer.assigned_region || '',
      coverageLgas: coverage.lgas || [],
      ratingValue: Number(officer.rating ?? officer.averageRating ?? officer.average_rating ?? 0),
      completionValue: Number(officer.completionRate ?? officer.completion_rate ?? 0),
      totalVisitCount: Number(officer.totalVisits ?? officer.total_visits ?? 0),
      assignmentCount: Number(officer.activeAssignments ?? officer.active_assignments ?? 0),
    };
  }), [careOfficers]);

  const filteredOfficers = officers.filter(officer => {
    const query = searchQuery.toLowerCase();
    const matchesSearch = !query ||
      officer.name.toLowerCase().includes(query) ||
      String(officer.email || '').toLowerCase().includes(query) ||
      String(officer.coverageState || '').toLowerCase().includes(query);
    const matchesStatus = statusFilter === 'all' || officer.statusLabel.toLowerCase().replace(/\s+/g, '-') === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusStyles = (status) => {
    const value = status.toLowerCase();
    if (value === 'active') return 'bg-green-100 text-green-700';
    if (value.includes('leave')) return 'bg-yellow-100 text-yellow-700';
    if (value.includes('deactiv')) return 'bg-gray-100 text-gray-600';
    if (value.includes('review') || value.includes('suspend') || value.includes('terminat')) return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-700';
  };

  const activeCount = officers.filter(o => o.statusLabel.toLowerCase() === 'active').length;
  const onLeaveCount = officers.filter(o => o.statusLabel.toLowerCase().includes('leave')).length;
  const avgRating = officers.length ? (officers.reduce((sum, o) => sum + o.ratingValue, 0) / officers.length).toFixed(1) : '0.0';
  const avgCompletion = officers.length ? (officers.reduce((sum, o) => sum + o.completionValue, 0) / officers.length).toFixed(1) : '0.0';

  const officerDetails = selectedOfficer ? (() => {
    const officerId = selectedOfficer.id || selectedOfficer._id;
    const officerVisits = visits.filter(visit => (visit.careOfficerId || visit.care_officer_id) === officerId);
    const dependantIds = new Set(officerVisits.map(visit => visit.dependantId || visit.dependant_id).filter(Boolean));
    return {
      visits: officerVisits,
      dependants: dependants.filter(dependant => dependantIds.has(dependant.id || dependant._id)),
      ratings: ratings.filter(rating => (rating.careOfficerId || rating.care_officer_id) === officerId),
    };
  })() : null;

  const runOfficerAction = async (officer, action) => {
    const officerId = officer.id || officer._id;
    if (!officerId) return;
    setActionLoading(`${action}-${officerId}`);
    try {
      const endpoint = action === 'reinstate'
        ? `/admin/care-officers/${officerId}/reinstate`
        : `/admin/care-officers/${officerId}/${action}`;
      await authApi.post(endpoint, { reason: `${action} by admin` });
      const actionLabels = { suspend: 'suspended', deactivate: 'deactivated', reinstate: 'reinstated' };
      toast.success(`Care officer ${actionLabels[action] || 'updated'}`);
      await fetchData({ force: true });
      setSelectedOfficer(null);
    } catch (error) {
      toast.error(getApiErrorMessage(error, `Unable to ${action} care officer`));
    } finally {
      setActionLoading('');
    }
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-officers-page">
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="font-serif text-2xl font-bold text-gray-900">Care Officers</h1>
              <p className="text-sm text-gray-500 mt-1">Manage all care officers across regions</p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/admin/users?create=care_officer')}
              className="px-4 py-2 bg-burgundy-600 text-[#FAFAF7] text-sm font-medium rounded-full hover:bg-burgundy-700 transition-colors flex items-center gap-2"
            >
              <UserPlus className="w-4 h-4" />
              Add Officer
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-5 mb-8">
          <StatCard label="Total Officers" value={officers.length} color="burgundy" icon={Users} />
          <StatCard label="Active Today" value={activeCount} color="green" icon={CheckCircle2} />
          <StatCard label="On Leave" value={onLeaveCount} color="yellow" icon={Clock} />
          <StatCard label="Avg. Rating" value={avgRating} color="blue" icon={Star} />
          <StatCard label="Avg. Completion" value={`${avgCompletion}%`} color="green" />
        </div>

        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, or region..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-2.5 border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-burgundy-500 focus:border-transparent"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 border border-gray-200 rounded-full text-sm bg-white focus:outline-none focus:ring-2 focus:ring-burgundy-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="on-leave">On Leave</option>
            <option value="under-review">Under Review</option>
            <option value="suspended">Suspended</option>
            <option value="deactivated">Deactivated</option>
            <option value="terminated">Terminated</option>
          </select>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Officer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Coverage</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Performance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assignments</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredOfficers.map((officer) => (
                  <tr key={officer.id || officer._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-burgundy-700 flex items-center justify-center text-[#FAFAF7] font-bold">
                          {officer.name.split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase()}
                        </div>
                        <div>
                          <button
                            type="button"
                            onClick={() => {
                              const userId = officer.userId || officer.user_id;
                              if (userId) setDetailUserId(userId);
                            }}
                            disabled={!(officer.userId || officer.user_id)}
                            className="text-left font-medium text-gray-900 hover:text-[#9F1D35] disabled:cursor-default disabled:hover:text-gray-900"
                          >
                            {officer.name}
                          </button>
                          <p className="text-xs text-gray-500">{officer.email || 'No email'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <span>{officer.coverageState || 'No coverage set'}</span>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">{officer.coverageLgas.length} LGAs</p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                          <span className="text-sm font-medium text-gray-900">{officer.ratingValue.toFixed(1)}</span>
                        </div>
                        <div className="text-sm text-gray-500">
                          <span className="text-green-600 font-medium">{officer.completionValue.toFixed(1)}%</span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">{officer.totalVisitCount} total visits</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-lg font-semibold text-gray-900">{officer.assignmentCount}</span>
                      <p className="text-xs text-gray-400">active</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${getStatusStyles(officer.statusLabel)}`}>
                        {officer.statusLabel}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setSelectedOfficer(officer)}
                          className="px-3 py-1.5 text-burgundy-600 hover:bg-burgundy-50 text-sm font-medium rounded-full transition-colors"
                        >
                          View
                        </button>
                        {officer.statusLabel.toLowerCase() === 'active' ? (
                          <button
                            type="button"
                            disabled={Boolean(actionLoading)}
                            onClick={() => runOfficerAction(officer, 'suspend')}
                            className="px-3 py-1.5 text-red-600 hover:bg-red-50 text-sm font-medium rounded-full transition-colors disabled:opacity-50"
                          >
                            Suspend
                          </button>
                        ) : (
                          <button
                            type="button"
                            disabled={Boolean(actionLoading)}
                            onClick={() => runOfficerAction(officer, 'reinstate')}
                            className="px-3 py-1.5 text-green-700 hover:bg-green-50 text-sm font-medium rounded-full transition-colors disabled:opacity-50"
                          >
                            Reinstate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredOfficers.length === 0 && (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900">No officers found</h3>
              <p className="text-sm text-gray-500">Try adjusting your search or filters</p>
            </div>
          )}
        </div>
      </div>

      {selectedOfficer && officerDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-6">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#9F1D35]">Care officer profile</p>
                <h2 className="mt-2 font-serif text-3xl font-semibold text-[#1A1A1A]">{selectedOfficer.name}</h2>
                <p className="mt-1 text-sm text-[#5A5A5A]">{selectedOfficer.email || 'No email'} · {selectedOfficer.phone || selectedOfficer.primaryPhone || 'No phone'}</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedOfficer(null)}
                className="rounded-full border border-[#D8D5D0] p-2 text-[#5A5A5A] hover:text-[#1A1A1A]"
                aria-label="Close officer details"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="grid gap-4 md:grid-cols-4">
              <div className="rounded-2xl border border-[#E8E6E3] bg-white p-4">
                <p className="text-xs uppercase tracking-[0.12em] text-[#5A5A5A]">Status</p>
                <p className="mt-2 font-semibold text-[#1A1A1A]">{selectedOfficer.statusLabel}</p>
              </div>
              <div className="rounded-2xl border border-[#E8E6E3] bg-white p-4">
                <p className="text-xs uppercase tracking-[0.12em] text-[#5A5A5A]">Visits</p>
                <p className="mt-2 font-semibold text-[#1A1A1A]">{officerDetails.visits.length}</p>
              </div>
              <div className="rounded-2xl border border-[#E8E6E3] bg-white p-4">
                <p className="text-xs uppercase tracking-[0.12em] text-[#5A5A5A]">Dependants</p>
                <p className="mt-2 font-semibold text-[#1A1A1A]">{officerDetails.dependants.length}</p>
              </div>
              <div className="rounded-2xl border border-[#E8E6E3] bg-white p-4">
                <p className="text-xs uppercase tracking-[0.12em] text-[#5A5A5A]">Rating</p>
                <p className="mt-2 font-semibold text-[#1A1A1A]">{selectedOfficer.ratingValue.toFixed(1)} ({officerDetails.ratings.length})</p>
              </div>
            </div>

            <div className="mt-6 grid gap-5 lg:grid-cols-[1fr_1.2fr]">
              <section className="rounded-2xl border border-[#E8E6E3] bg-white p-5">
                <h3 className="font-serif text-xl font-semibold text-[#1A1A1A]">Coverage and KYC</h3>
                <dl className="mt-4 space-y-3 text-sm">
                  <div>
                    <dt className="text-[#5A5A5A]">State</dt>
                    <dd className="font-medium text-[#1A1A1A]">{selectedOfficer.coverageState || selectedOfficer.state || 'Not set'}</dd>
                  </div>
                  <div>
                    <dt className="text-[#5A5A5A]">LGAs</dt>
                    <dd className="font-medium text-[#1A1A1A]">{selectedOfficer.coverageLgas.length ? selectedOfficer.coverageLgas.join(', ') : 'Not set'}</dd>
                  </div>
                  <div>
                    <dt className="text-[#5A5A5A]">Shift</dt>
                    <dd className="font-medium text-[#1A1A1A]">{selectedOfficer.preferredShift || selectedOfficer.preferred_shift || 'Not set'}</dd>
                  </div>
                  <div>
                    <dt className="text-[#5A5A5A]">Capacity</dt>
                    <dd className="font-medium text-[#1A1A1A]">{selectedOfficer.maxVisitsPerWeek || selectedOfficer.max_visits_per_week || 'Not set'} visits/week</dd>
                  </div>
                </dl>
              </section>

              <section className="rounded-2xl border border-[#E8E6E3] bg-white p-5">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <h3 className="font-serif text-xl font-semibold text-[#1A1A1A]">Recent visits</h3>
                  <button
                    type="button"
                    onClick={() => navigate(`/visits?status=all`)}
                    className="rounded-full border border-[#9F1D35] px-3 py-1.5 text-xs font-medium text-[#9F1D35]"
                  >
                    View all visits
                  </button>
                </div>
                <div className="space-y-3">
                  {officerDetails.visits.slice(0, 5).map(visit => (
                    <button
                      key={visit.id || visit._id}
                      type="button"
                      onClick={() => navigate(`/visits/${visit.id || visit._id}`)}
                      className="flex w-full items-center justify-between gap-3 rounded-xl border border-[#E8E6E3] px-4 py-3 text-left hover:border-[#D8D5D0]"
                    >
                      <span>
                        <span className="block font-medium text-[#1A1A1A]">{visit.dependantName || visit.dependantSnapshot?.name || 'Dependant visit'}</span>
                        <span className="mt-0.5 flex items-center gap-1 text-xs text-[#5A5A5A]"><CalendarDays className="h-3.5 w-3.5" />{visit.scheduledStart || visit.scheduledDate ? new Date(visit.scheduledStart || visit.scheduledDate).toLocaleDateString() : 'Date not set'}</span>
                      </span>
                      <span className="rounded-full bg-[#F5E8EA] px-2.5 py-1 text-xs font-medium text-[#9F1D35]">{visit.status || 'Scheduled'}</span>
                    </button>
                  ))}
                  {officerDetails.visits.length === 0 && <p className="text-sm text-[#5A5A5A]">No visits assigned yet.</p>}
                </div>
              </section>
            </div>

            <div className="mt-6 flex flex-col gap-3 border-t border-[#E8E6E3] pt-5 sm:flex-row sm:justify-end">
              {selectedOfficer.statusLabel.toLowerCase() === 'active' ? (
                <>
                  <button
                    type="button"
                    disabled={Boolean(actionLoading)}
                    onClick={() => runOfficerAction(selectedOfficer, 'suspend')}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-red-300 px-5 py-3 text-sm font-medium text-red-700 disabled:opacity-50"
                  >
                    <ShieldOff className="h-4 w-4" />
                    Suspend Officer
                  </button>
                  <button
                    type="button"
                    disabled={Boolean(actionLoading)}
                    onClick={() => runOfficerAction(selectedOfficer, 'deactivate')}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-red-700 px-5 py-3 text-sm font-medium text-[#FAFAF7] disabled:opacity-50"
                  >
                    Deactivate Officer
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  disabled={Boolean(actionLoading)}
                  onClick={() => runOfficerAction(selectedOfficer, 'reinstate')}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#9F1D35] px-5 py-3 text-sm font-medium text-[#FAFAF7] disabled:opacity-50"
                >
                  <RotateCcw className="h-4 w-4" />
                  Reinstate Officer
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <AdminUserDetailModal
        userId={detailUserId}
        open={Boolean(detailUserId)}
        onOpenChange={(open) => !open && setDetailUserId('')}
      />
    </div>
  );
};

export default AdminOfficersPage;
